import { useSyncExternalStore } from "react";

// Cart lives in the visitor's browser: [{ productId, quantity }]. Prices are never stored
// here; the server prices the cart from the database at checkout.
const STORAGE_KEY = "mizu-cart";
export const MAX_ITEM_QUANTITY = 20;

const listeners = new Set();
let items = readStorage();

function readStorage() {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || "[]");
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((item) => typeof item?.productId === "string" && Number.isInteger(item.quantity))
      .map((item) => ({
        productId: item.productId,
        quantity: Math.min(MAX_ITEM_QUANTITY, Math.max(1, item.quantity)),
      }));
  } catch {
    return [];
  }
}

function commit(next) {
  items = next;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    // Private mode / storage blocked: the cart still works for this page view.
  }
  listeners.forEach((listener) => listener());
}

function subscribe(listener) {
  listeners.add(listener);

  // Keep several open tabs in sync.
  function onStorage(event) {
    if (event.key !== STORAGE_KEY) return;
    items = readStorage();
    listener();
  }
  window.addEventListener("storage", onStorage);

  return () => {
    listeners.delete(listener);
    window.removeEventListener("storage", onStorage);
  };
}

function getSnapshot() {
  return items;
}

export function addToCart(productId, quantity = 1) {
  const existing = items.find((item) => item.productId === productId);
  if (existing) {
    setCartQuantity(productId, existing.quantity + quantity);
  } else {
    commit([...items, { productId, quantity: Math.min(MAX_ITEM_QUANTITY, quantity) }]);
  }
}

export function setCartQuantity(productId, quantity) {
  if (quantity < 1) {
    removeFromCart(productId);
    return;
  }
  commit(
    items.map((item) =>
      item.productId === productId
        ? { ...item, quantity: Math.min(MAX_ITEM_QUANTITY, quantity) }
        : item,
    ),
  );
}

export function removeFromCart(productId) {
  commit(items.filter((item) => item.productId !== productId));
}

/** Drops lines whose product no longer exists in the shop. */
export function pruneCart(knownProductIds) {
  const known = new Set(knownProductIds);
  const next = items.filter((item) => known.has(item.productId));
  if (next.length !== items.length) commit(next);
}

export function clearCart() {
  commit([]);
}

export function useCart() {
  const cartItems = useSyncExternalStore(subscribe, getSnapshot, () => []);
  const count = cartItems.reduce((sum, item) => sum + item.quantity, 0);
  return { items: cartItems, count };
}

/** Joins cart lines with product data; unknown products are skipped. */
export function buildCartLines(cartItems, products, lang) {
  const byId = new Map(products.map((product) => [product.id, product]));
  return cartItems
    .map((item) => {
      const product = byId.get(item.productId);
      if (!product) return null;
      return {
        productId: product.id,
        name: (lang === "en" && product.nameEn) || product.nameHu,
        unitLabel: product.unitLabel,
        imageUrl: product.imageUrl,
        priceHuf: product.priceHuf,
        available: product.available,
        quantity: item.quantity,
        lineTotalHuf: product.priceHuf * item.quantity,
      };
    })
    .filter(Boolean);
}
